/*
This useAnalytics.js hook is a comprehensive analytics tracking system 
for this React application. 

Here's what it does:
Core Purpose
Tracks user behavior, 
page views, 
events, 
and errors across multiple analytics providers
with environment-specific configurations.

*/
// hooks/useAnalytics.js
import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

// Configuration for different environments
const ANALYTICS_CONFIG = {
  development: {
    enabled: true,
    providers: ['console'],
    endpoint: null // Disable custom endpoint in development to avoid 405 errors
  },
  staging: {
    enabled: true,
    providers: ['console', 'azure'],
    endpoint: 'https://analytics.eevolve.ai/api/events' // Use correct API endpoint
  },
  production: {
    enabled: true,
    providers: ['azure', 'google', 'amplitude'],
    endpoint: 'https://analytics.eevolve.ai/api/events' // Use correct API endpoint
  }
};

// Enhanced CSP validation with proper endpoint checking
const isEndpointAllowed = (endpoint) => {
  if (!endpoint) return false;
  
  const allowedDomains = [
    'https://analytics.eevolve.ai',
    'https://eevolve-backend-app.azurewebsites.net',
    'https://dc.services.visualstudio.com',
    'https://www.google-analytics.com',
    'https://api.amplitude.com',
    window.location.origin
  ];
  
  // Allow relative URLs
  if (endpoint.startsWith('/') || endpoint.startsWith('./')) {
    return true;
  }
  
  try {
    const endpointUrl = new URL(endpoint);
    return allowedDomains.some(domain => {
      try {
        const domainUrl = new URL(domain);
        return endpointUrl.hostname === domainUrl.hostname ||
               endpointUrl.hostname.endsWith('.' + domainUrl.hostname);
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
};

// Get current environment configuration
const getConfig = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_STAGE === 'staging' 
      ? ANALYTICS_CONFIG.staging 
      : ANALYTICS_CONFIG.production;
  }
  return ANALYTICS_CONFIG.development;
};

// Check if provider is available
const isProviderAvailable = (provider) => {
  switch (provider) {
    case 'google':
      return typeof window.gtag === 'function';
    case 'azure':
      return typeof window.appInsights === 'object' && window.appInsights.trackPageView;
    case 'amplitude':
      return typeof window.amplitude === 'object' && typeof window.amplitude.logEvent === 'function';
    case 'console':
      return true;
    default:
      return false;
  }
};

// Safe axios instance with better error handling
const analyticsAxios = axios.create({
  timeout: 3000, // Reduced timeout to 3 seconds
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor to validate endpoints before sending
analyticsAxios.interceptors.request.use(
  (config) => {
    if (!isEndpointAllowed(config.url)) {
      throw new Error(`CSP Violation: Endpoint ${config.url} is not allowed`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const useAnalytics = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth() || {};
  const config = useRef(getConfig());
  const previousPath = useRef('');
  const providersInitialized = useRef(false);
  const availableProviders = useRef(new Set());

  // Initialize analytics providers
  useEffect(() => {
    if (!config.current.enabled || providersInitialized.current) return;

    const initProviders = () => {
      try {
        const enabledProviders = config.current.providers || [];
        const available = new Set();

        enabledProviders.forEach(provider => {
          if (isProviderAvailable(provider)) {
            available.add(provider);
            console.log(`[Analytics] Provider ${provider} initialized successfully`);
          } else {
            console.warn(`[Analytics] Provider ${provider} is not available`);
          }
        });

        availableProviders.current = available;
        providersInitialized.current = true;

        if (available.size === 0) {
          console.warn('[Analytics] No analytics providers available. Analytics disabled.');
        } else {
          console.log(`[Analytics] Initialized providers:`, Array.from(available));
        }
      } catch (error) {
        console.error('[Analytics] Failed to initialize analytics providers:', error);
        providersInitialized.current = true; // Mark as initialized to prevent retries
      }
    };

    // Initialize immediately if document is ready, otherwise wait for load
    if (document.readyState === 'complete') {
      initProviders();
    } else {
      window.addEventListener('load', initProviders);
      return () => window.removeEventListener('load', initProviders);
    }
  }, []);

  // Safe endpoint tracking function
  const trackToEndpoint = useCallback(async (data, endpoint) => {
    if (!endpoint || !isEndpointAllowed(endpoint)) {
      console.warn('[Analytics] Endpoint not configured or not allowed:', endpoint);
      return;
    }

    try {
      // Validate endpoint URL format
      const url = new URL(endpoint);
      if (!url.protocol.startsWith('http')) {
        throw new Error('Invalid endpoint protocol');
      }

      await analyticsAxios.post(endpoint, data);
    } catch (error) {
      // Don't log 405 errors as they're expected if endpoint doesn't exist
      if (error.response && error.response.status === 405) {
        console.warn('[Analytics] Endpoint does not accept POST requests:', endpoint);
      } else if (error.code === 'ERR_NETWORK') {
        console.warn('[Analytics] Network error reaching endpoint:', endpoint);
      } else if (!error.message.includes('CSP Violation')) {
        // Only log non-CSP errors
        console.warn('[Analytics] Endpoint error:', error.message);
      }
    }
  }, []);

  // Track page views
  useEffect(() => {
    if (!config.current.enabled || location.pathname === previousPath.current) return;

    const pagePath = location.pathname + location.search;
    previousPath.current = pagePath;

    // Prepare base event data
    const eventData = {
      path: pagePath,
      timestamp: new Date().toISOString(),
      userId: user?._id || 'anonymous',
      isAuthenticated: !!isAuthenticated,
      environment: process.env.NODE_ENV,
      stage: process.env.REACT_APP_STAGE || 'development',
      referrer: document.referrer || ''
    };

    // Send to all configured providers
    const trackPageView = () => {
      if (availableProviders.current.size === 0) return;

      // Console (dev only)
      if (availableProviders.current.has('console')) {
        console.log('[Analytics] Page view:', eventData);
      }

      // Google Analytics
      if (availableProviders.current.has('google') && process.env.REACT_APP_GA_TRACKING_ID) {
        try {
          window.gtag('event', 'page_view', {
            page_title: document.title,
            page_location: window.location.href,
            page_path: eventData.path,
            user_id: eventData.userId
          });
        } catch (error) {
          console.warn('[Analytics] Google Analytics error:', error);
        }
      }

      // Azure Application Insights
      if (availableProviders.current.has('azure')) {
        try {
          window.appInsights.trackPageView({
            name: document.title,
            uri: eventData.path,
            properties: eventData
          });
        } catch (error) {
          console.warn('[Analytics] Azure App Insights error:', error);
        }
      }

      // Amplitude
      if (availableProviders.current.has('amplitude')) {
        try {
          window.amplitude.logEvent('Page View', {
            ...eventData,
            page_title: document.title,
            url: window.location.href
          });
        } catch (error) {
          console.warn('[Analytics] Amplitude error:', error);
        }
      }

      // Custom analytics endpoint
      if (config.current.endpoint) {
        trackToEndpoint({
          type: 'pageview',
          name: 'page_view',
          data: eventData,
          timestamp: new Date().toISOString()
        }, config.current.endpoint);
      }
    };

    // Add a small delay to ensure providers are initialized
    if (providersInitialized.current) {
      trackPageView();
    } else {
      const timer = setTimeout(() => {
        if (providersInitialized.current) {
          trackPageView();
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [location, user, isAuthenticated, trackToEndpoint]);

  // Track custom events
  const trackEvent = useCallback((eventName, eventProperties = {}) => {
    if (!config.current.enabled || availableProviders.current.size === 0) {
      if (config.current.enabled) {
        console.warn(`[Analytics] Event "${eventName}" not tracked - no providers available`);
      }
      return;
    }

    // Prepare event data
    const eventData = {
      ...eventProperties,
      event: eventName,
      timestamp: new Date().toISOString(),
      userId: user?._id || 'anonymous',
      isAuthenticated: !!isAuthenticated,
      environment: process.env.NODE_ENV,
      page: window.location.pathname,
      url: window.location.href
    };

    try {
      // Console (dev only)
      if (availableProviders.current.has('console')) {
        console.log(`[Analytics] Event: ${eventName}`, eventData);
      }

      // Google Analytics
      if (availableProviders.current.has('google') && process.env.REACT_APP_GA_TRACKING_ID) {
        window.gtag('event', eventName, eventData);
      }

      // Azure Application Insights
      if (availableProviders.current.has('azure')) {
        window.appInsights.trackEvent({
          name: eventName,
          properties: eventData
        });
      }

      // Amplitude
      if (availableProviders.current.has('amplitude')) {
        window.amplitude.logEvent(eventName, eventData);
      }

      // Custom analytics endpoint
      if (config.current.endpoint) {
        trackToEndpoint({
          type: 'event',
          name: eventName,
          data: eventData,
          timestamp: new Date().toISOString()
        }, config.current.endpoint);
      }
    } catch (error) {
      console.warn('[Analytics] Event tracking error:', error);
    }
  }, [user, isAuthenticated, trackToEndpoint]);

  // Track errors
  const trackError = useCallback((error, errorInfo = null) => {
    if (!config.current.enabled || availableProviders.current.size === 0) return;

    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      userId: user?._id || 'anonymous',
      isAuthenticated: !!isAuthenticated,
      environment: process.env.NODE_ENV,
      page: window.location.pathname,
      url: window.location.href
    };

    try {
      // Console (dev only)
      if (availableProviders.current.has('console')) {
        console.error('[Analytics] Error:', errorData);
      }

      // Azure Application Insights
      if (availableProviders.current.has('azure')) {
        window.appInsights.trackException({
          exception: new Error(error.message),
          properties: errorData
        });
      }

      // Custom analytics endpoint
      if (config.current.endpoint) {
        trackToEndpoint({
          type: 'error',
          name: 'error',
          data: errorData,
          timestamp: new Date().toISOString()
        }, config.current.endpoint);
      }
    } catch (trackingError) {
      console.warn('[Analytics] Error tracking error:', trackingError);
    }
  }, [user, isAuthenticated, trackToEndpoint]);

  // Track user registration
  const trackUserRegistration = useCallback((userData, registrationMethod = 'default') => {
    if (!config.current.enabled || availableProviders.current.size === 0) {
      console.warn('[Analytics] User registration not tracked - analytics disabled or no providers available');
      return;
    }
    
    const registrationData = {
      ...userData,
      registrationMethod,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      stage: process.env.REACT_APP_STAGE || 'development',
      page: window.location.pathname,
      url: window.location.href
    };

    try {
      // Console (dev only)
      if (availableProviders.current.has('console')) {
        console.log('[Analytics] User Registration:', registrationData);
      }

      // Google Analytics
      if (availableProviders.current.has('google') && process.env.REACT_APP_GA_TRACKING_ID) {
        window.gtag('event', 'sign_up', {
          method: registrationMethod,
          user_id: userData._id
        });
      }

      // Azure Application Insights
      if (availableProviders.current.has('azure')) {
        window.appInsights.trackEvent({
          name: 'UserRegistration',
          properties: registrationData
        });
        
        // Set authenticated user context
        if (userData._id) {
          window.appInsights.setAuthenticatedUserContext(userData._id);
        }
      }

      // Amplitude
      if (availableProviders.current.has('amplitude')) {
        window.amplitude.logEvent('User Registered', registrationData);
        
        // Set user ID for future events
        if (userData._id) {
          window.amplitude.setUserId(userData._id);
        }
      }

      // Custom analytics endpoint
      if (config.current.endpoint) {
        trackToEndpoint({
          type: 'user_registration',
          name: 'user_registration',
          data: registrationData,
          timestamp: new Date().toISOString()
        }, config.current.endpoint);
      }
    } catch (error) {
      console.warn('[Analytics] Registration tracking error:', error);
    }
  }, [trackToEndpoint]);

  return { 
    trackEvent, 
    trackError, 
    trackUserRegistration,
    isEnabled: config.current.enabled,
    providers: Array.from(availableProviders.current)
  };
};

export default useAnalytics;
